import React from 'react'
import { motion } from 'framer-motion'
import { useAnimations } from '../contexts/AnimationContext'
import { FigmaCard, FigmaButton } from '../components/FigmaUIElements'
import { Target, MapPin, Trophy, Calendar, Zap, Star } from 'lucide-react'

const Missions: React.FC = () => {
  const { settings } = useAnimations()

  return (
    <motion.div
      className="min-h-screen p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-8"
          animate={settings.floatingElements ? {
            y: [0, -5, 0],
            transition: { duration: 4, repeat: Infinity, ease: 'easeInOut' }
          } : {}}
        >
          <h1 className="text-4xl font-bold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent mb-4">
            Mission Control
          </h1>
          <p className="text-gray-300 text-lg">
            Accept missions, complete objectives, and earn rewards across the galaxy.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <FigmaCard className="p-6">
            <div className="flex items-center mb-4">
              <Target className="w-8 h-8 text-yellow-400 mr-3" />
              <h3 className="text-xl font-semibold text-white">Active Missions</h3>
            </div>
            <div className="text-3xl font-bold text-yellow-400 mb-2">7</div>
            <p className="text-gray-300">Currently in progress</p>
          </FigmaCard>

          <FigmaCard className="p-6">
            <div className="flex items-center mb-4">
              <Trophy className="w-8 h-8 text-green-400 mr-3" />
              <h3 className="text-xl font-semibold text-white">Completed</h3>
            </div>
            <div className="text-3xl font-bold text-green-400 mb-2">142</div>
            <p className="text-gray-300">Total missions completed</p>
          </FigmaCard>

          <FigmaCard className="p-6">
            <div className="flex items-center mb-4">
              <Star className="w-8 h-8 text-purple-400 mr-3" />
              <h3 className="text-xl font-semibold text-white">Reputation</h3>
            </div>
            <div className="text-3xl font-bold text-purple-400 mb-2">Elite</div>
            <p className="text-gray-300">Current status level</p>
          </FigmaCard>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <FigmaCard className="p-6">
            <h3 className="text-2xl font-semibold text-white mb-4">Available Missions</h3>
            <div className="space-y-4">
              {[
                {
                  title: 'Escort Convoy Alpha-7',
                  type: 'Combat',
                  reward: '25,000 Credits',
                  difficulty: 'Medium',
                  location: 'Vega System',
                  timeLimit: '48h'
                },
                {
                  title: 'Archaeological Survey',
                  type: 'Exploration',
                  reward: '18,500 Credits',
                  difficulty: 'Easy',
                  location: 'Kepler-442',
                  timeLimit: '72h'
                },
                {
                  title: 'Pirate Base Assault',
                  type: 'Combat',
                  reward: '45,000 Credits',
                  difficulty: 'Hard',
                  location: 'Asteroid Belt Delta',
                  timeLimit: '24h'
                }
              ].map((mission, index) => (
                <motion.div
                  key={mission.title}
                  className="p-4 bg-gray-800/50 rounded-lg"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-white font-medium">{mission.title}</h4>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      mission.difficulty === 'Easy' ? 'bg-green-500/20 text-green-400' :
                      mission.difficulty === 'Medium' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {mission.difficulty}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                    <div>
                      <span className="text-gray-400">Type: </span>
                      <span className="text-white">{mission.type}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Reward: </span>
                      <span className="text-yellow-400">{mission.reward}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Location: </span>
                      <span className="text-white">{mission.location}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Time Limit: </span>
                      <span className="text-white">{mission.timeLimit}</span>
                    </div>
                  </div>
                  <FigmaButton variant="primary" className="w-full">
                    Accept Mission
                  </FigmaButton>
                </motion.div>
              ))}
            </div>
          </FigmaCard>

          <FigmaCard className="p-6">
            <h3 className="text-2xl font-semibold text-white mb-4">Mission Progress</h3>
            <div className="space-y-4">
              {[
                {
                  title: 'Supply Run Beta-3',
                  progress: 75,
                  objective: 'Deliver cargo to 3/4 stations',
                  timeRemaining: '6h 30m'
                },
                {
                  title: 'Diplomatic Escort',
                  progress: 45,
                  objective: 'Safely transport ambassador',
                  timeRemaining: '12h 15m'
                },
                {
                  title: 'Resource Collection',
                  progress: 90,
                  objective: 'Mine 900/1000 units',
                  timeRemaining: '2h 45m'
                }
              ].map((progress, index) => (
                <motion.div
                  key={progress.title}
                  className="p-4 bg-gray-800/30 rounded-lg"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-white font-medium">{progress.title}</h4>
                    <span className="text-gray-400 text-sm">{progress.timeRemaining}</span>
                  </div>
                  <p className="text-gray-300 text-sm mb-3">{progress.objective}</p>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <motion.div
                      className="bg-gradient-to-r from-yellow-500 to-orange-500 h-2 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${progress.progress}%` }}
                      transition={{ duration: 1, delay: index * 0.2 }}
                    />
                  </div>
                  <div className="text-right text-sm text-yellow-400 mt-1">
                    {progress.progress}% Complete
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="mt-6 pt-6 border-t border-gray-700">
              <h4 className="text-lg font-medium text-white mb-3">Daily Challenges</h4>
              <div className="space-y-2">
                {[
                  { challenge: 'Win 3 combat encounters', reward: '5,000 XP', completed: true },
                  { challenge: 'Trade 50 units of resources', reward: '3,000 Credits', completed: false },
                  { challenge: 'Explore 2 new sectors', reward: '10,000 XP', completed: false }
                ].map((daily, index) => (
                  <div key={daily.challenge} className="flex items-center justify-between text-sm">
                    <span className={`${daily.completed ? 'text-green-400 line-through' : 'text-gray-300'}`}>
                      {daily.challenge}
                    </span>
                    <span className="text-yellow-400">{daily.reward}</span>
                  </div>
                ))}
              </div>
            </div>
          </FigmaCard>
        </div>
      </div>
    </motion.div>
  )
}

export default Missions